import Table from '@shared/components/Table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { companiesService } from '@shared/services/companiesService';
import { levelOfStructureService } from '@modules/organizationalStructure/levelOfStructure/services/levelOfStructure';
import { structuralGroupService } from '@modules/organizationalStructure/structuralGroups/services/structuralGroups';
import { useTranslation } from 'react-i18next';
import LoadingButton from '@shared/components/LoadingButton';

export default function StructuralGroupMoving() {
    const { t } = useTranslation();
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [sortBy, setSortBy] = useState('fromDate');
    const [sortDir, setSortDir] = useState('asc');
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [structuralGroup, setStructuralGroup] = useState(null);
    const [companies, setCompanies] = useState([]);
    const [companiesLoading, setCompaniesLoading] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [fullCompanyObject, setFullCompanyObject] = useState(null);
    const [levels, setLevels] = useState([]);
    const [levelsLoading, setLevelsLoading] = useState(false);
    const [selectedLevel, setSelectedLevel] = useState(null);
    const [movingData, setMovingData] = useState([]);
    const [movingDataLoading, setMovingDataLoading] = useState(false);
    const [showSubordinateTable, setShowSubordinateTable] = useState(false);
    const [fromDate, setFromDate] = useState(new Date().toISOString().split('T')[0]);
    const [toDate, setToDate] = useState('');
    const [subordinateData, setSubordinateData] = useState([]);
    const [subordinateDataLoading, setSubordinateDataLoading] = useState(false);
    const [isSubordinateDrillDownMode, setIsSubordinateDrillDownMode] = useState(false);
    const [subordinateDrillDownData, setSubordinateDrillDownData] = useState([]);
    const [subordinateBreadcrumbPath, setSubordinateBreadcrumbPath] = useState([]);
    const [subordinateParentId, setSubordinateParentId] = useState(null);
    const [selectedSubordinateRecord, setSelectedSubordinateRecord] = useState(null);
    const [searchQuery, setSearchQuery] = useState({});
    const [searchType, setSearchType] = useState('');

    useEffect(() => {
        if (location.state?.structuralGroup) {
            setStructuralGroup(location.state.structuralGroup);
        }
    }, [location.state]);

    useEffect(() => {
        const fetchMovingData = async () => {
            if (!structuralGroup?.id) {
                setMovingData([]);
                return;
            }

            try {
                setMovingDataLoading(true);
                const response = await structuralGroupService.getStructuralGroupMoving(structuralGroup.id);
                setMovingData(response);
            } catch (err) {
                console.error('Error fetching moving data:', err);
                setError('Failed to load moving data');
                setMovingData([]);
            } finally {
                setMovingDataLoading(false);
            }
        };

        fetchMovingData();
    }, [structuralGroup?.id]);

    useEffect(() => {
        const fetchCompanies = async () => {
            try {
                setCompaniesLoading(true);
                const response = await companiesService.getAllCompanies();
                setCompanies(response);
                if (response && response.length > 0) {
                    setSelectedCompany(response[0].id);
                    setFullCompanyObject(response[0]);
                }
            } catch (err) {
                console.error('Error fetching companies:', err);
                setError('Failed to load companies');
            } finally {
                setCompaniesLoading(false);
            }
        };

        fetchCompanies();
    }, []);

    useEffect(() => {
        const fetchLevels = async () => {
            if (!selectedCompany) {
                setLevels([]);
                setSelectedLevel(null);
                return;
            }

            try {
                setLevelsLoading(true);
                setSelectedLevel(null);
                const response = await levelOfStructureService.getLevels(selectedCompany);
                setLevels(response);
            } catch (err) {
                console.error('Error fetching levels:', err);
                setLevels([]);
            } finally {
                setLevelsLoading(false);
            }
        };

        fetchLevels();
    }, [selectedCompany]);

    const fetchSubordinateData = async () => {
        if (!showSubordinateTable || !selectedCompany || !selectedLevel) {
            setSubordinateData([]);
            return;
        }

        try {
            setSubordinateDataLoading(true);

            const searchParams = { parentId: selectedLevel };
            if (searchQuery.code) {
                searchParams.code = searchQuery.code;
            }
            if (searchQuery.title) {
                searchParams.longDescription = searchQuery.title;
            }
            if (searchQuery.date) {
                searchParams.date = searchQuery.date;
            }

            const response = await levelOfStructureService.getOrgUnitsByLevel(selectedLevel);
            setSubordinateData(response);
        } catch (err) {
            console.error('❌ Error fetching subordinate data:', err);
            setError('Failed to load subordinate data');
            setSubordinateData([]);
        } finally {
            setSubordinateDataLoading(false);
        }
    };

    useEffect(() => {
        fetchSubordinateData();
    }, [showSubordinateTable, selectedCompany, selectedLevel]);

    const handleSubordinateRowClick = async (index) => {
        const currentData = isSubordinateDrillDownMode ? subordinateDrillDownData : subordinateData;

        if (index >= 0 && index < currentData.length) {
            const structuralGroup = currentData[index];

            try {
                setSubordinateDataLoading(true);

                const response = await levelOfStructureService.getOrgUnitById(selectedCompany, { parentId: structuralGroup.id });

                setSubordinateDrillDownData(response);
                setIsSubordinateDrillDownMode(true);
                setSubordinateParentId(structuralGroup.id);
                setSubordinateBreadcrumbPath(prev => [...prev, {
                    id: structuralGroup.id,
                    title: structuralGroup.longDescription || structuralGroup.code,
                    level: structuralGroup.hrOrgUnitLevels?.planLevel
                }]);
            } catch (err) {
                console.error('❌ Error fetching drill-down data:', err);
                setError('Failed to load drill-down data');
                setSubordinateDrillDownData([]);
                setIsSubordinateDrillDownMode(false);
            } finally {
                setSubordinateDataLoading(false);
            }
        } else {
            console.error('Invalid index:', index, 'Data length:', currentData.length);
        }
    };

    const handleSubordinateBreadcrumbClick = async (breadcrumbItem, index) => {
        try {
            setSubordinateDataLoading(true);

            if (breadcrumbItem.id === 'root' || index === 0) {
                setIsSubordinateDrillDownMode(false);
                setSubordinateDrillDownData([]);
                setSubordinateParentId(null);
                setSubordinateBreadcrumbPath([]);
            } else {
                const actualIndex = index - 1;
                const targetItem = subordinateBreadcrumbPath[actualIndex];

                if (targetItem) {
                    const response = await levelOfStructureService.getOrgUnitById(selectedCompany, { parentId: targetItem.id });
                    setSubordinateDrillDownData(response);
                    setIsSubordinateDrillDownMode(true);
                    setSubordinateParentId(targetItem.id);
                    setSubordinateBreadcrumbPath(prev => prev.slice(0, actualIndex + 1));
                }
            }
        } catch (err) {
            console.error('Error navigating to breadcrumb level:', err);
            setError('Failed to navigate to breadcrumb level');
        } finally {
            setSubordinateDataLoading(false);
        }
    };

    const handleSubordinateCheckboxChange = (index) => {
        const currentData = isSubordinateDrillDownMode ? subordinateDrillDownData : subordinateData;
        if (index >= 0 && index < currentData.length) {
            const record = currentData[index];
            const newSelection = selectedSubordinateRecord?.id === record.id ? null : record;
            setSelectedSubordinateRecord(newSelection);
        }
    };

    const handleSearch = () => {
        fetchSubordinateData();
    };

    const handleCreateMoving = async () => {
        if (!selectedSubordinateRecord) {
            setError('Please select a subordinate record');
            return;
        }

        if (!fromDate) {
            setError('Please select a from date');
            return;
        }

        try {
            setLoading(true);
            setLoading(true);
            setError(null);

            const movingData = {
                hrOrgUnitById: structuralGroup?.id,
                hrOrgUnitByParentId: selectedSubordinateRecord.id,
                fromDate: fromDate
            };


            await structuralGroupService.createStructuralGroupMoving(movingData);

            // Refresh the moving data to show the new record
            const response = await structuralGroupService.getStructuralGroupMoving(structuralGroup.id);
            setMovingData(response);

            // Reset form
            setSelectedSubordinateRecord(null);
            setFromDate(new Date().toISOString().split('T')[0]);
            setToDate('');
            setShowSubordinateTable(false);
            setIsSubordinateDrillDownMode(false);
            setSubordinateDrillDownData([]);
            setSubordinateBreadcrumbPath([]);
            setSubordinateParentId(null);

        } catch (err) {
            console.error('❌ Error creating structural group moving:', err);
            setError(err.response?.data?.message || 'Failed to create structural group moving');
        } finally {
            setLoading(false);
        }
    };


    const columns = [
        {
            key: 'fromDate',
            label: t('common.fromDate'),
        },
        {
            key: 'toDate',
            label: t('common.toDate'),
            render: (item) => item.toDate === '2999-01-01' ? t('organizationalStructure.structuralGroups.form.indefinite') : item.toDate
        },
        {
            key: 'hrOrgUnitByParentId',
            label: t('organizationalStructure.structuralGroups.form.subordinationTo'),
            render: (item) => item.hrOrgUnitByParentId?.longDescription || item.hrOrgUnitByParentId?.code || ''
        }
    ];

    const columnsSubordinate = [
        {
            key: 'planLevel', label: t('organizationalStructure.structuralGroups.table.headers.level'),
            render: (item) => item.hrOrgUnitLevels?.planLevel || ''
        },
        {
            key: 'code',
            label: t('organizationalStructure.structuralGroups.table.headers.code'),
        },
        {
            key: 'longDescription',
            label: t('organizationalStructure.structuralGroups.table.headers.name'),
        }
    ];

    const searchOptions = [
        {
            key: 'title',
            label: 'title',
        },
        {
            key: 'code',
            label: 'code',
        },
        {
            key: "date",
            label: 'date',
        }
    ];


    const actionButtons = (
        <>
            <button
                className="icon-button"
                onClick={() => setShowSubordinateTable(true)}
                title={t('common.add')}
            >
                <FontAwesomeIcon icon={faPlus} />
            </button>
        </>
    )

    return (
        <div className="flex-column w-100">
            <Table
                data={movingData}
                loading={movingDataLoading}
                error={error}
                selectedData={selectedRecord}
                setSelectedData={setSelectedRecord}
                sortBy={sortBy}
                sortDir={sortDir}
                columns={columns}
                showPagination={false}
                showPageSizeSelector={false}
                showCustomSearch={true}
                actionButtons={actionButtons}
                title={`${t('organizationalStructure.structuralGroups.structuralGroupMoving')}: ${structuralGroup?.longDescription || structuralGroup?.code || ''}`}
            />
            {showSubordinateTable && (
                <Table
                    data={isSubordinateDrillDownMode ? subordinateDrillDownData : subordinateData}
                    loading={subordinateDataLoading}
                    error={error}
                    selectedData={selectedSubordinateRecord?.id}
                    sortBy={sortBy}
                    sortDir={sortDir}
                    columns={columnsSubordinate}
                    searchOptions={searchOptions}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    setSearchType={setSearchType}
                    handleSearchClick={handleSearch}
                    getData={fetchSubordinateData}
                    showPagination={false}
                    showPageSizeSelector={false}
                    showCustomSearch={false}
                    showPreviousPage={false}
                    showCompanySelect={true}
                    companyOptions={companies}
                    showLevelSelect={true}
                    selectedCompany={selectedCompany}
                    setSelectedCompany={setSelectedCompany}
                    selectedLevel={selectedLevel}
                    setSelectedLevel={setSelectedLevel}
                    levelOptions={levels}
                    levelsLoading={levelsLoading}
                    handleRowClick={handleSubordinateRowClick}
                    showCheckbox={true}
                    handleCheckboxChange={handleSubordinateCheckboxChange}
                    showBreadcrumb={isSubordinateDrillDownMode}
                    breadcrumbPath={isSubordinateDrillDownMode ? [
                        { id: 'root', title: fullCompanyObject?.name || 'Root' },
                        ...subordinateBreadcrumbPath
                    ] : []}
                    onBreadcrumbClick={isSubordinateDrillDownMode ? handleSubordinateBreadcrumbClick : null}
                    title={`${t('organizationalStructure.structuralGroups.form.subordinationTo')}`}
                />
            )}

            {showSubordinateTable && (
                <div className="modal-footer">
                    {error && (
                        <div className="alert alert-danger" style={{ marginBottom: '15px' }}>
                            {error}
                        </div>
                    )}
                    <div className='flex-row'>
                        <span className='nowrap'>{t('common.fromDate')}:</span>
                        <input className='search-input'
                            type="date"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)} />
                    </div>
                    <div className='flex-row'>
                        <span className='nowrap'>{t('organizationalStructure.structuralGroups.form.subordinationTo')}:</span>
                        <input
                            className='search-input'
                            type="text"
                            value={selectedSubordinateRecord ? (selectedSubordinateRecord.longDescription || selectedSubordinateRecord.code || '') : ''}
                            onChange={(e) => setToDate(e.target.value)}
                            placeholder={t('common.selectRecord')}
                            readOnly
                            style={{
                                backgroundColor: selectedSubordinateRecord ? '#e8f5e8' : '#f8f9fa',
                                borderColor: selectedSubordinateRecord ? '#28a745' : '#ced4da'
                            }}
                        />
                    </div>
                    <LoadingButton
                        type="button"
                        className="btn btn-primary"
                        onClick={handleCreateMoving}
                        disabled={loading}
                    >
                        {loading ? 'Creating...' : t('common.save')}
                    </LoadingButton>
                    <button type="button" className="btn btn-secondary" onClick={() => {
                        setShowSubordinateTable(false);
                        setIsSubordinateDrillDownMode(false);
                        setSubordinateDrillDownData([]);
                        setSubordinateBreadcrumbPath([]);
                        setSubordinateParentId(null);
                        setSelectedSubordinateRecord(null);
                        setFromDate(new Date().toISOString().split('T')[0]);
                    }}>
                        {t('common.cancel')}
                    </button>
                </div>
            )}
        </div>
    );
}